import { DonationProps, SearchSelectOption } from '@classy/campaign-page-blocks'
import { whammyFetch } from 'classy-api-proxies/whammy'
import {
  CampaignProgramDesignation,
  GetCampaignProgramDesignationsResponse,
} from 'services/designations'
import { logger } from 'utils/logger'

/**
 * Maps a CampaignProgramDesignation entity to a SearchSelectOption entity.
 */
export const toSearchSelectOption = (
  designation: CampaignProgramDesignation,
): SearchSelectOption => ({
  id: designation.id,
  label: designation.name,
})

/**
 * Maps a list of CampaignProgramDesignation entities to a list of SearchSelectOption entities,
 * including grouping them by their designation group.
 */
export const toSearchSelectOptions = (
  designations: CampaignProgramDesignation[],
): SearchSelectOption[] => {
  const options: Record<SearchSelectOption['id'], SearchSelectOption> = {}

  designations.forEach((designation) => {
    const groupId = designation.designationGroupId
    if (groupId in options) {
      const group = options[groupId]
      if (group.subOptions) {
        group.subOptions.push(toSearchSelectOption(designation))
      }
    } else {
      options[groupId] = {
        id: groupId,
        label: designation.designationGroupName,
        subOptions: [toSearchSelectOption(designation)],
      }
    }
  })

  return Object.values(options)
}

export interface FetchProgramDesignationsParams {
  campaignId: string
  searchString?: string
  page?: number
}

/**
 * Fetch program designations.
 */
export const fetchProgramDesignations = async ({
  campaignId,
  searchString = '',
  page = 1,
}: FetchProgramDesignationsParams): Promise<GetCampaignProgramDesignationsResponse> => {
  try {
    const response = await whammyFetch('/search/campaign-designations', {
      method: 'POST',
      body: JSON.stringify({ campaignId, searchString, page }),
    })
    const json = await response.json()
    return json
  } catch (e) {
    logger('error', new Error('Unable to fetch program designations', { cause: e }), {
      context: { campaignId, searchString, page },
    })
    throw e
  }
}

/**
 * Generates the fetch request for fetching program designations and transforming them into a
 * SearchSelectOption list, appropriate for being passed to the Donation block's SearchSelect
 * component.
 *
 * If there is any error, the initial program designations are returned.
 */
export const useFetchProgramDesignations = (
  campaignId: DonationProps['campaignId'],
  initialProgramDesignations: DonationProps['initialProgramDesignations'] = [],
) => {
  const fetch = async (searchString = '', page = 1): Promise<SearchSelectOption[]> => {
    if (!campaignId) {
      logger('error', 'Campaign ID is required to fetch program designations')
      return initialProgramDesignations
    }

    try {
      const { designations } = await fetchProgramDesignations({
        campaignId,
        searchString,
        page,
      })
      return toSearchSelectOptions(designations)
    } catch (e) {
      logger(
        'error',
        new Error('Donation controller unable to fetch program designations', { cause: e }),
        {
          context: { campaignId, searchString },
        },
      )
      return initialProgramDesignations
    }
  }

  return {
    fetchProgramDesignations: fetch,
  }
}
